import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Cultaz | Data Protection & Privacy Information</title>
        <meta name="description" content="Cultaz Privacy Policy - Learn how we collect, use, and protect your data. Comprehensive privacy information for our retail management software. Last updated January 2026." />
        <meta name="keywords" content="Cultaz privacy policy, data protection, privacy policy, retail software privacy, data security, Nigeria data privacy" />
        <meta name="author" content="Cultaz" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Privacy Policy - Cultaz" />
        <meta property="og:description" content="Cultaz Privacy Policy - Learn how we collect, use, and protect your data in our retail management software." />
        <meta property="og:url" content="https://cultaz.com/privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cultaz" />
        <link rel="canonical" href="https://cultaz.com/privacy" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
      <main className="container mx-auto px-6 lg:px-12 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 1, 2026</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
              <p className="text-muted-foreground mb-4">
                Cultaz ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our retail management software and services, including Cultaz Cloud and Cultaz Desktop POS (collectively, "Services").
              </p>
              <p className="text-muted-foreground">
                Please read this Privacy Policy carefully. By using our Services, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3">1. Personal Information</h3>
              <p className="text-muted-foreground mb-4">
                When you create an account or use our Services, we may collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
                <li>Name, email address, and contact information</li>
                <li>Business name, address, and registration details</li>
                <li>Payment and billing information (processed securely through third-party payment processors)</li>
                <li>Account credentials and authentication information</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">2. Business Data</h3>
              <p className="text-muted-foreground mb-4">
                To provide our retail management services, we collect and process:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
                <li>Product information, inventory data, and stock levels</li>
                <li>Sales transactions and receipts</li>
                <li>Customer information and purchase history</li>
                <li>Employee and staff data for multi-user accounts</li>
                <li>Financial reports and analytics</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">3. Usage Information</h3>
              <p className="text-muted-foreground mb-4">
                We automatically collect certain information when you use our Services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
                <li>Device information (operating system, hardware specifications)</li>
                <li>IP address and location data</li>
                <li>Usage patterns, features accessed, and interaction data</li>
                <li>Error logs and diagnostic information</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">4. Desktop Application Data</h3>
              <p className="text-muted-foreground mb-6">
                For Cultaz Desktop POS users, data is stored locally on your device. We only access this data when:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>You explicitly choose to sync data with Cultaz Cloud</li>
                <li>You request technical support that requires data access</li>
                <li>Automatic updates require license verification</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use the collected information for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>To provide, maintain, and improve our Services</li>
                <li>To process transactions and manage your account</li>
                <li>To send you updates, security alerts, and administrative messages</li>
                <li>To respond to your inquiries and provide customer support</li>
                <li>To detect, prevent, and address technical issues and security threats</li>
                <li>To comply with legal obligations and enforce our terms of service</li>
                <li>To analyze usage patterns and improve user experience</li>
                <li>To send marketing communications (with your consent)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Data Storage and Security</h2>
              
              <h3 className="text-xl font-semibold mb-3">Cloud Services</h3>
              <p className="text-muted-foreground mb-4">
                Data stored in Cultaz Cloud is encrypted in transit and at rest. We use industry-standard security measures including SSL/TLS encryption, secure databases, and regular security audits.
              </p>

              <h3 className="text-xl font-semibold mb-3">Desktop Application</h3>
              <p className="text-muted-foreground mb-6">
                Cultaz Desktop POS stores data locally on your device using SQLite databases. Your data remains on your premises unless you choose to sync with our cloud services. Local data is encrypted using AES-256 encryption for sensitive information.
              </p>

              <p className="text-muted-foreground">
                While we implement reasonable security measures, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Data Sharing and Disclosure</h2>
              <p className="text-muted-foreground mb-4">We do not sell your personal information. We may share your information only in the following circumstances:</p>
              
              <h3 className="text-xl font-semibold mb-3">Service Providers</h3>
              <p className="text-muted-foreground mb-4">
                We may share information with third-party service providers who assist us in operating our Services, such as payment processors, hosting providers, and analytics services. These providers are contractually obligated to protect your information.
              </p>

              <h3 className="text-xl font-semibold mb-3">Legal Requirements</h3>
              <p className="text-muted-foreground mb-4">
                We may disclose your information if required by law, court order, or government regulation, or to protect our rights, property, or safety, or that of our users.
              </p>

              <h3 className="text-xl font-semibold mb-3">Business Transfers</h3>
              <p className="text-muted-foreground mb-6">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Your Rights and Choices</h2>
              <p className="text-muted-foreground mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a structured format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Desktop Data Control:</strong> For Desktop POS users, your data remains under your control and can be deleted locally at any time</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                To exercise these rights, please contact us at <a href="mailto:comfort@cultaz.com" className="text-primary hover:underline">comfort@cultaz.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-muted-foreground mb-4">
                We use cookies and similar technologies to enhance your experience, analyze usage, and assist with marketing efforts. You can control cookies through your browser settings, though this may affect some functionality of our Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Data Retention</h2>
              <p className="text-muted-foreground mb-4">
                We retain your personal information for as long as necessary to provide our Services and comply with legal obligations. When you delete your account, we will delete or anonymize your data in accordance with applicable laws, except for data we are required to retain for legal, tax, or regulatory purposes.
              </p>
              <p className="text-muted-foreground">
                For Desktop POS users, data stored locally remains on your device until you delete it or uninstall the application.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Children's Privacy</h2>
              <p className="text-muted-foreground mb-6">
                Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete such information promptly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">International Data Transfers</h2>
              <p className="text-muted-foreground mb-6">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Changes to This Privacy Policy</h2>
              <p className="text-muted-foreground mb-6">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground mb-2"><strong>Email:</strong> <a href="mailto:comfort@cultaz.com" className="text-primary hover:underline">comfort@cultaz.com</a></p>
                <p className="text-muted-foreground mb-2"><strong>Phone:</strong> +234 201 330 9313</p>
                <p className="text-muted-foreground"><strong>Business Hours:</strong> Monday - Friday, 8:00 AM - 6:00 PM (WAT)</p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default Privacy;
